import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

const ScrollContainer = ({
  children,
  containerClass,
  onReachedEnd,
  onReachedEndDelay,
}) => {
  const ref = useRef();
  const [canFireCallback, setCanFireCallback] = useState(true);
  const [lastChildHeight, setLastChildHeight] = useState(0);

  useEffect(() => {
    const container = ref.current;
    const lastChild = container?.lastElementChild;

    if (lastChild) {
      setLastChildHeight(lastChild.offsetHeight);
    }
  }, []);

  const handleScroll = useCallback(
    e => {
      if (!canFireCallback) return;

      const { scrollTop, clientHeight, scrollHeight } = e.target;

      if (scrollTop + clientHeight >= scrollHeight - 15) {
        onReachedEnd();
        setCanFireCallback(false);
      }
    },
    [canFireCallback, onReachedEnd],
  );

  useEffect(() => {
    const currentRef = ref.current;
    if (currentRef) {
      currentRef.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (currentRef) {
        currentRef.removeEventListener('scroll', handleScroll);
      }
    };
  }, [handleScroll]);

  useEffect(() => {
    let timeoutId;
    if (!canFireCallback) {
      timeoutId = setTimeout(() => {
        setCanFireCallback(true);
      }, onReachedEndDelay);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [canFireCallback, onReachedEndDelay]);

  return (
    <div
      className={containerClass}
      ref={ref}
      style={{ '--last-child-height': `${lastChildHeight}px` }}
    >
      {children}
    </div>
  );
};

ScrollContainer.propTypes = {
  children: PropTypes.node.isRequired,
  onReachedEnd: PropTypes.func.isRequired,
  onReachedEndDelay: PropTypes.number,
  containerClass: PropTypes.string,
};

ScrollContainer.defaultProps = {
  onReachedEndDelay: 1000,
  containerClass: 'scroll-container',
};

export default ScrollContainer;
